import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import PropTypes from "prop-types";
import { forwardRef, useCallback, useState } from "react";

import { careerSimulationType } from "@features/career-details/types";
import { Badge, Slide, SubHeading } from "@features/ui";

import { formatDate, ONE_DIGIT_DATE_FORMATTER } from "@lib/date.helpers";
import { aOrAn } from "@lib/text.helpers";
import { buttonTracking } from "@lib/tracking.helpers";

import { useCareerSimulationLoginCode } from "@features/career-details/api/use-career-simulation-login-code";
import { ClassroomSelectionModal } from "@features/career-details/components/classroom-selection.modal";
import { HeadsetImage } from "@features/career-details/components/headset-image";
import { SimPassButton } from "@features/career-details/components/sim-pass.button";
import { useSelectedClassroom } from "@features/career-details/hooks/use-selected-classroom";

export const SimulationSlide = forwardRef(
  (
    { careerSimulation: { relevance, simulation }, completedAt, className },
    elementRef,
  ) => {
    const [isSelectingClassroom, setIsSelectingClassroom] = useState(false);

    const {
      classrooms,
      selectedClassroom,
      classroomId,
      needsSelection,
      selectClassroom,
      isLoading: isLoadingClassrooms,
    } = useSelectedClassroom();

    const {
      data: alternativeLoginCode,
      isPending: isGeneratingSimPass,
      mutate: generateSimPass,
    } = useCareerSimulationLoginCode(simulation.code);

    const onGetSimPass = useCallback(() => {
      if (needsSelection) {
        setIsSelectingClassroom(true);
        return;
      }

      generateSimPass({ classroomId });
    }, [needsSelection, classroomId, generateSimPass]);

    // Until the classrooms land we don't know whether to prompt, and generating
    // now would send no classroom at all.
    const isSimPassUnavailable = isLoadingClassrooms || isGeneratingSimPass;

    const onClassroomSelected = useCallback(
      (selectedClassroomId) => {
        setIsSelectingClassroom(false);
        selectClassroom({ classroomId: selectedClassroomId });
        generateSimPass({ classroomId: selectedClassroomId });
      },
      [selectClassroom, generateSimPass],
    );

    return (
      <Slide ref={elementRef} className={clsx("simulation-slide", className)}>
        {relevance === "exact" ? (
          <>
            <SubHeading tabIndex={-1}>Get a Closer Look</SubHeading>
            <div className="info">Try this career out in virtual reality!</div>
          </>
        ) : (
          <>
            <SubHeading tabIndex={-1}>Not a perfect match but...</SubHeading>
            <div className="info">
              Practice related skills as {aOrAn(simulation.careerTitle)}.
            </div>
          </>
        )}
        <HeadsetImage simulationCode={simulation.code} relevance={relevance} />
        <p className="simulation-name">Simulation: {simulation.name}</p>
        <div className="sim-pass-container">
          <SimPassButton
            alternativeLoginCode={alternativeLoginCode}
            isGeneratingCode={isSimPassUnavailable}
            onClick={onGetSimPass}
          />
          {classrooms.length > 1 && selectedClassroom && (
            <button
              type="button"
              className="change-classroom-button"
              onClick={() => setIsSelectingClassroom(true)}
              {...buttonTracking("career-details", "change-classroom")}
            >
              {selectedClassroom.name}
            </button>
          )}
        </div>
        <ClassroomSelectionModal
          open={isSelectingClassroom}
          selected={classroomId}
          classrooms={classrooms}
          onSelect={onClassroomSelected}
          onClose={() => setIsSelectingClassroom(false)}
        />
        {completedAt && (
          <Badge className="completed-badge">
            <FontAwesomeIcon icon={["fa-solid", "fa-check-circle"]} />
            Complete{" "}
            {formatDate({
              date: completedAt,
              formatter: ONE_DIGIT_DATE_FORMATTER,
              separator: "/",
            })}
          </Badge>
        )}
      </Slide>
    );
  },
);

SimulationSlide.displayName = "Simulation Slide";

SimulationSlide.propTypes = {
  careerSimulation: careerSimulationType,
  completedAt: PropTypes.instanceOf(Date),
  className: PropTypes.string,
};
